import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { trainerApi, type Announcement } from '@/lib/api/trainer';
import { queryKeys } from '@/lib/queryKeys';
import { AnnouncementComposer } from '@/features/Trainer/AnnouncementComposer';

export const Route = createFileRoute('/trainer/batches/$batchId/announcements')({
  component: AnnouncementsTab,
});

function relativeTime(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  if (hours < 48) return 'Yesterday';
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function AnnouncementRow({ a }: { a: Announcement }) {
  return (
    <div className="rounded-xl border border-[#ececec] bg-white px-5 py-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs font-semibold text-[#111827]">{a.creator_name || 'Trainer'}</span>
        <span className="text-[11px] text-[#9ca3af]">{relativeTime(a.created_at)}</span>
      </div>
      <p className="text-sm text-[#111827] leading-relaxed whitespace-pre-wrap break-words">{a.content}</p>
    </div>
  );
}

export function AnnouncementsTab() {
  const { batchId } = Route.useParams();

  const { data: announcements = [], isLoading } = useQuery<Announcement[]>({
    queryKey: queryKeys.trainer.batchAnnouncements(batchId),
    queryFn: async () => {
      const { data } = await trainerApi.listAnnouncements(batchId);
      return data;
    },
  });

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <div className="flex-1 overflow-y-auto p-6 max-w-2xl">

        {/* Composer */}
        <div className="mb-6 rounded-xl border border-[#ececec] bg-white p-4">
          <p className="mb-3 text-sm font-semibold text-[#111827]">Post an announcement</p>
          <AnnouncementComposer batchId={batchId} />
        </div>

        {/* Feed */}
        {isLoading && (
          <div className="flex h-32 items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-4 border-[#ff5a1f] border-t-transparent" />
          </div>
        )}

        {!isLoading && announcements.length === 0 && (
          <div className="rounded-xl border border-dashed border-[#ececec] p-10 text-center">
            <p className="text-sm font-medium text-[#111827]">No announcements yet</p>
            <p className="mt-1 text-xs text-[#6b7280]">Post your first announcement above to notify all students in this batch.</p>
          </div>
        )}

        {!isLoading && announcements.length > 0 && (
          <div className="space-y-3">
            {announcements.map((a) => (
              <AnnouncementRow key={a.id} a={a} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
